import { offerWindow } from 'helpers/offerWindow';
import { leagueGroupName } from 'helpers/leagueGroupName';
import { useModernDesktop } from 'components/Layout/ModernDesktopShell';
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import styles from '../styles/pages/LeaguesList.module.css';
import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import { capitalizeFirstWord } from 'helpers/stringHelper';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { League } from 'types/League';
import useAsyncEffect from 'use-async-effect';
import {
  getLeagues,
  getLeaguesForSidebar,
  searchEventsByTeamName,
} from 'services/api/sportBetsService';
import { Loader } from 'components/Loader/Loader';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useTranslation } from 'react-i18next';
import { sportTranslationKey } from 'helpers/sportI18n';
import { SearchResults } from 'components/mobile/SearchResults/SearchResults';
import { PrematchCatalogContext } from 'context/prematchCatalogContext';
import { filterPrematchSearchBySport } from 'helpers/filterPrematchSearchBySport';
import type { MatchOffer } from 'types/MatchOffer';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { CountryFlag } from 'components/TeamCrest/TeamCrest';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import { usePlayerLayout } from 'lib/playerLayout';
import { VipRoundButton } from 'components/mobile/VipChrome/VipChrome';
import { VipIcon } from 'components/mobile/VipChrome/VipIcons';
import classNames from 'classnames';

const SEARCH_DEBOUNCE_MS = 350;
/** VIP: this many top leagues in the sideways row. */
const VIP_TOP_LEAGUES = 8;
const MIN_SEARCH_QUERY_LENGTH = 2;

type SidebarLeague = {
  name: string;
  id: number;
  count: number;
};

type SidebarGroup = {
  name: string;
  id: number;
  count: number;
  cc?: string;
  subList: SidebarLeague[];
};

// Open groups per sport, kept for the session (sessionStorage may be blocked).
const expandedKey = (sportId: number) => `leaguesExpanded:${sportId}`;

const readExpandedGroups = (sportId: number): Record<number, boolean> => {
  try {
    return JSON.parse(sessionStorage.getItem(expandedKey(sportId)) ?? '{}');
  } catch {
    return {};
  }
};

const writeExpandedGroups = (
  sportId: number,
  groups: Record<number, boolean>
) => {
  try {
    sessionStorage.setItem(expandedKey(sportId), JSON.stringify(groups));
  } catch {
    // Remembering is a convenience; the list works without it.
  }
};

const lastLeagueKey = (sportId: number) => `leaguesLast:${sportId}`;

const rememberLastLeague = (sportId: number, leagueId: number) => {
  try {
    sessionStorage.setItem(lastLeagueKey(sportId), String(leagueId));
  } catch {
    // A convenience only.
  }
};

const readLastLeague = (sportId: number): string | null => {
  try {
    return sessionStorage.getItem(lastLeagueKey(sportId));
  } catch {
    return null;
  }
};

export const LeaguesList = () => {
  const { lastPrematchPush } = useContext(PrematchCatalogContext);
  const { t, i18n } = useTranslation();
  const urlParts = useLocation().pathname.split('/');
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const modernDesktop = useModernDesktop();
  const vip = usePlayerLayout() === 'vip';
  const [topLeagues, setTopLeagues] = useState<League[]>([]);
  const sportId = urlParts.pop() ?? '1';
  const sport = urlParts.pop()!;

  const sportIdNumber = useMemo(() => Number.parseInt(sportId, 10), [sportId]);

  const prematchRefreshKey = useMemo(() => {
    if (!lastPrematchPush?.sportIds.includes(sportIdNumber)) {
      return 0;
    }
    return lastPrematchPush.version;
  }, [lastPrematchPush, sportIdNumber]);

  const [searchParams] = useSearchParams();
  const time = searchParams.get('time');
  // ?time= is an offer window key (helpers/offerWindow).
  const offer = useMemo(() => offerWindow(time), [time]);
  const dateTime = offer.to;

  const [groupedLeagues, setGroupedLeagues] = useState<SidebarGroup[] | null>(
    null
  );
  const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>(
    {}
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);

  const [searchResults, setSearchResults] = useState<Map<
    number,
    MatchOffer[]
  > | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [emptySearchQuery, setEmptySearchQuery] = useState<string | null>(null);

  useAsyncEffect(async () => {
    const leaguesData = await getLeaguesForSidebar(
      sportIdNumber,
      dateTime,
      false,
      offer.from
    );
    if (!leaguesData) {
      setGroupedLeagues(null);
      return;
    }

    const groups: SidebarGroup[] = leaguesData.every((item: any) =>
      Array.isArray(item?.subList)
    )
      ? leaguesData
      : [
          {
            name: t('league'),
            id: -sportIdNumber,
            count: leaguesData.length,
            subList: (leaguesData as League[]).map((league) => ({
              name: league.name,
              id: league.id,
              count: league.count,
            })),
          },
        ];

    setGroupedLeagues(groups);
    // Groups the player had open (e.g. International) stay open when they come
    // back from a league, instead of the list folding up to the top again.
    const remembered = readExpandedGroups(sportIdNumber);
    setExpandedGroups(
      groups.reduce(
        (acc, group) => ({ ...acc, [group.id]: remembered[group.id] ?? false }),
        {}
      )
    );
  }, [sportIdNumber, dateTime, offer.from, prematchRefreshKey, t]);

  // VIP: the sport's top leagues one tap from the start of the list.
  useAsyncEffect(
    async (isMounted) => {
      if (!vip) return;
      const l = await getLeagues(sportIdNumber, dateTime).catch(() => null);
      if (isMounted()) setTopLeagues(Array.isArray(l) ? l : []);
    },
    [vip, sportIdNumber, dateTime, prematchRefreshKey]
  );

  const openLeague = (league: { id: number; name: string }) => {
    rememberLastLeague(sportIdNumber, league.id);
    navigate(
      `/sports-betting/${sport}/${sportId}/${league.name}/${league.id}${time ? `?time=${time}` : ''}`
    );
  };

  // Back from a league: bring that league into view again.
  useEffect(() => {
    if (!groupedLeagues) return;
    const last = readLastLeague(sportIdNumber);
    if (last == null) return;
    document
      .querySelector(`[data-league-id="${last}"]`)
      ?.scrollIntoView({ block: 'center' });
  }, [groupedLeagues, sportIdNumber]);

  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    } else {
      setSearchQuery('');
    }
  }, [searchOpen]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!searchOpen) {
      searchSeqRef.current += 1;
      setSearchResults(null);
      setEmptySearchQuery(null);
      setSearchLoading(false);
      return;
    }

    const q = searchQuery.trim();
    if (q.length < MIN_SEARCH_QUERY_LENGTH) {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      searchSeqRef.current += 1;
      setSearchResults(null);
      setEmptySearchQuery(null);
      setSearchLoading(false);
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      const mySeq = ++searchSeqRef.current;
      const query = searchQuery.trim();
      setSearchLoading(true);

      void (async () => {
        try {
          const raw = await searchEventsByTeamName(query, false, true);
          if (mySeq !== searchSeqRef.current) return;
          if (!raw) {
            setSearchResults(null);
            setEmptySearchQuery(query);
            setSearchLoading(false);
            return;
          }
          const filtered = filterPrematchSearchBySport(
            raw as Map<number, MatchOffer[]>,
            sportIdNumber
          );
          if (filtered.size > 0) {
            setSearchResults(filtered);
            setEmptySearchQuery(null);
          } else {
            setSearchResults(null);
            setEmptySearchQuery(query);
          }
        } catch {
          if (mySeq !== searchSeqRef.current) return;
          setSearchResults(null);
          setEmptySearchQuery(query);
        }
        setSearchLoading(false);
      })();
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [searchOpen, searchQuery, sportIdNumber]);

  if (!isMobile && !modernDesktop) {
    return <Navigate to="/not-found" />;
  }

  const showMinCharsHint =
    searchOpen &&
    searchQuery.trim().length > 0 &&
    searchQuery.trim().length < MIN_SEARCH_QUERY_LENGTH;

  return (
    <MobileLayout
      pageTitle={t(sportTranslationKey(sportIdNumber), {
        defaultValue: capitalizeFirstWord(sport),
      })}
      headerTrailing={
        vip ? (
          <VipRoundButton
            label={t('search_label')}
            on={searchOpen}
            onClick={() => setSearchOpen((o) => !o)}
          >
            <VipIcon name="search" />
          </VipRoundButton>
        ) : (
          <button
            type="button"
            className={styles.searchToggle}
            onClick={() => setSearchOpen((o) => !o)}
            aria-expanded={searchOpen}
            aria-label={t('search_label')}
          >
            <ThemeIcon
              icon="search"
              alt=""
              variant="inline"
              tone="chrome"
              width={22}
              height={22}
            />
          </button>
        )
      }
      topContent={
        searchOpen ? (
          <div className={styles.searchPanel}>
            <input
              ref={searchInputRef}
              type="search"
              className={styles.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('search_matches_placeholder')}
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
        ) : null
      }
    >
      {!searchOpen && (
        <div className={styles.wrapper}>
          {vip && topLeagues.length > 0 && (
            <div className={styles.vipTop}>
              {topLeagues.slice(0, VIP_TOP_LEAGUES).map((l) => (
                <button type="button" key={l.id} onClick={() => openLeague(l)}>
                  {l.cc && (
                    <CountryFlag cc={l.cc} className={styles.vipTopFlag} />
                  )}
                  {l.display_name || l.name}
                </button>
              ))}
            </div>
          )}
          {!groupedLeagues && <Loader />}
          {groupedLeagues &&
            groupedLeagues.map((group) => (
              <div
                key={group.id}
                className={classNames(
                  vip && styles.vipGroup,
                  vip && expandedGroups[group.id] && styles.vipGroupOpen
                )}
              >
                <div
                  className={styles.countryWrapper}
                  onClick={() =>
                    setExpandedGroups((prev) => {
                      const next = { ...prev, [group.id]: !prev[group.id] };
                      writeExpandedGroups(sportIdNumber, next);
                      return next;
                    })
                  }
                >
                  {group.cc ? (
                    <CountryFlag cc={group.cc} className={styles.countryFlag} />
                  ) : (
                    sport && (
                      <SportIcon
                        sportId={sportId}
                        tone="content"
                        className={styles.countrySportIcon}
                        alt={sport}
                      />
                    )
                  )}
                  <div className={styles.title}>
                    {leagueGroupName(
                      group.name,
                      (group as any).groupCode,
                      i18n.language
                    )}
                  </div>
                  <div className={styles.count}>{group.count}</div>
                  {vip && (
                    <VipIcon
                      name="chevron"
                      size={12}
                      className={styles.vipChevron}
                    />
                  )}
                </div>
                {expandedGroups[group.id] &&
                  group.subList.map((league) => (
                    <div
                      key={league.id}
                      className={styles.leagueWrapper}
                      data-league-id={league.id}
                      onClick={() => openLeague(league)}
                    >
                      <div className={styles.leagueTitle}>{league.name}</div>
                      <div className={styles.count}>{league.count}</div>
                    </div>
                  ))}
              </div>
            ))}
        </div>
      )}

      {searchOpen && (
        <div className={styles.searchBody}>
          {showMinCharsHint && (
            <div className={styles.searchHint}>
              <ThemeIcon
                icon="info-circle"
                alt=""
                variant="inline"
                tone="content"
              />
              <span>{t('betting_search_error')}</span>
            </div>
          )}
          {searchLoading &&
            searchQuery.trim().length >= MIN_SEARCH_QUERY_LENGTH && <Loader />}
          {!searchLoading &&
            searchResults &&
            searchResults.size > 0 &&
            searchQuery.trim().length >= MIN_SEARCH_QUERY_LENGTH && (
              <SearchResults events={searchResults} liveSearch={false} />
            )}
          {!searchLoading &&
            emptySearchQuery &&
            searchQuery.trim().length >= MIN_SEARCH_QUERY_LENGTH && (
              <div className={styles.emptySearchInline}>
                <ThemeIcon
                  icon="info-circle"
                  alt=""
                  variant="inline"
                  tone="content"
                />
                <span>
                  {t('no_search_results').replace(
                    '{search_query}',
                    emptySearchQuery
                  )}
                </span>
              </div>
            )}
        </div>
      )}
    </MobileLayout>
  );
};
